<template>
  <div class="workspace-view">
    <!-- 顶部导航栏 -->
    <div class="workspace-header">
      <div class="header-left">
        <el-button @click="goBack" text>
          <el-icon><ArrowLeft /></el-icon>
          返回项目列表
        </el-button>
        <el-divider direction="vertical" />
        <div class="project-info">
          <h2 class="project-title">{{ projectStore.project?.project_name || '加载中...' }}</h2>
          <el-tag :type="getStatusType(projectStore.project?.status)" size="small">
            {{ getStatusLabel(projectStore.project?.status) }}
          </el-tag>
        </div>
      </div>
      <div class="header-right">
        <el-button-group>
          <el-button @click="showProjectInfo = true">
            <el-icon><InfoFilled /></el-icon>
            项目信息
          </el-button>
          <el-button type="primary" @click="saveProject">
            <el-icon><Check /></el-icon>
            保存
          </el-button>
        </el-button-group>
      </div>
    </div>

    <!-- 工作区主体 -->
    <div class="workspace-body">
      <!-- 左侧步骤导航 -->
      <div class="step-sidebar">
        <div class="step-list">
          <div
            v-for="(step, index) in steps"
            :key="step.key"
            class="step-item"
            :class="{
              'active': currentStep === step.key,
              'completed': isStepCompleted(step.key),
              'disabled': !isStepAvailable(step.key)
            }"
            @click="selectStep(step.key)"
          >
            <div class="step-number">{{ index + 1 }}</div>
            <div class="step-content">
              <div class="step-title">{{ step.title }}</div>
              <div class="step-desc">{{ step.description }}</div>
            </div>
            <el-icon v-if="isStepCompleted(step.key)" class="step-check"><Check /></el-icon>
          </div>
        </div>

        <!-- 项目进度 -->
        <div class="progress-section">
          <h4>整体进度</h4>
          <el-progress 
            :percentage="progressPercent" 
            :status="progressStatus"
            :stroke-width="10"
          />
          <p class="progress-text">{{ progressText }}</p>
        </div>
      </div>

      <!-- 右侧内容区 -->
      <div class="content-area">
        <div class="content-header">
          <h3>{{ currentStepTitle }}</h3>
          <p class="step-hint">{{ currentStepHint }}</p>
        </div>

        <div class="content-body">
          <!-- 步骤内容组件 -->
          <component 
            :is="currentStepComponent" 
            v-if="currentStepComponent"
            :project-id="projectId"
            @step-complete="handleStepComplete"
            @step-save="handleStepSave"
          />
        </div>

        <!-- 底部操作栏 -->
        <div class="content-footer">
          <el-button 
            v-if="hasPrevStep" 
            @click="goToPrevStep"
          >
            <el-icon><ArrowLeft /></el-icon>
            上一步
          </el-button>
          <el-button
            v-if="hasNextStep"
            type="primary"
            @click="goToNextStep"
          >
            下一步
            <el-icon><ArrowRight /></el-icon>
          </el-button>
          <el-button
            v-else-if="currentStep === 'compose'"
            type="success"
            :loading="composing"
            @click="composeFinalVideo"
          >
            <el-icon><VideoPlay /></el-icon>
            生成最终视频
          </el-button>
        </div>
      </div>
    </div>

    <!-- 项目信息对话框 -->
    <el-dialog
      v-model="showProjectInfo"
      title="项目信息"
      width="500px"
    >
      <el-descriptions :column="1" border>
        <el-descriptions-item label="项目名称">
          {{ projectStore.project?.project_name }}
        </el-descriptions-item>
        <el-descriptions-item label="项目描述">
          {{ projectStore.project?.description || '无描述' }}
        </el-descriptions-item>
        <el-descriptions-item label="当前状态">
          <el-tag :type="getStatusType(projectStore.project?.status)">
            {{ getStatusLabel(projectStore.project?.status) }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="创建时间">
          {{ formatDate(projectStore.project?.created_at) }}
        </el-descriptions-item>
        <el-descriptions-item label="更新时间">
          {{ formatDate(projectStore.project?.updated_at) }}
        </el-descriptions-item>
      </el-descriptions>
      <template #footer>
        <el-button @click="showProjectInfo = false">关闭</el-button>
        <el-button type="primary" @click="editProject">编辑</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, markRaw } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useProjectStore } from '../stores/project'
import { ElMessage, ElMessageBox } from 'element-plus'

// 导入步骤组件
import TextStep from '../components/steps/TextStep.vue'
import SettingsStep from '../components/steps/SettingsStep.vue'
import SettingGalleryStep from '../components/steps/SettingGalleryStep.vue'
import ScriptStep from '../components/steps/ScriptStep.vue'
import VoiceStep from '../components/steps/VoiceStep.vue'
import StoryboardStep from '../components/steps/StoryboardStep.vue'
import VideosStep from '../components/steps/VideosStep.vue'
import ComposeStep from '../components/steps/ComposeStep.vue'

const route = useRoute()
const router = useRouter()
const projectStore = useProjectStore()

// 项目ID
const projectId = computed(() => parseInt(route.params.projectId))

// 当前步骤
const currentStep = ref('text')
const composing = ref(false)
const showProjectInfo = ref(false)

// 步骤定义
const steps = [
  {
    key: 'text',
    title: '原文管理',
    description: '上传或编辑小说原文',
    component: markRaw(TextStep),
    requiredStatus: 'draft'
  },
  {
    key: 'settings',
    title: '设定管理',
    description: '提取和管理角色设定',
    component: markRaw(SettingsStep),
    requiredStatus: 'text_uploaded'
  },
  {
    key: 'settingGallery',
    title: '设定图集',
    description: '生成设定参考图',
    component: markRaw(SettingGalleryStep),
    requiredStatus: 'settings_extracted'
  },
  {
    key: 'script',
    title: '剧本拆解',
    description: '生成场景化剧本',
    component: markRaw(ScriptStep),
    requiredStatus: 'setting_images_generated'
  },
  {
    key: 'voice',
    title: '语音合成',
    description: '生成配音音频',
    component: markRaw(VoiceStep),
    requiredStatus: 'script_generated'
  },
  {
    key: 'storyboard',
    title: '分镜生成',
    description: '生成宫格分镜大图、扩图、视频',
    component: markRaw(StoryboardStep),
    requiredStatus: 'voice_generated'
  },
  {
    key: 'videos',
    title: '视频生成',
    description: '生成视频片段',
    component: markRaw(VideosStep),
    requiredStatus: 'images_generated'
  },
  {
    key: 'compose',
    title: '视频合成',
    description: '合成最终视频',
    component: markRaw(ComposeStep),
    requiredStatus: 'videos_generated'
  }
]

// 当前步骤信息
const currentStepInfo = computed(() => steps.find(s => s.key === currentStep.value))
const currentStepTitle = computed(() => currentStepInfo.value?.title || '')
const currentStepHint = computed(() => currentStepInfo.value?.description || '')
const currentStepComponent = computed(() => currentStepInfo.value?.component || null)

// 步骤导航
const currentStepIndex = computed(() => steps.findIndex(s => s.key === currentStep.value))
const hasPrevStep = computed(() => currentStepIndex.value > 0)
const hasNextStep = computed(() => currentStepIndex.value < steps.length - 1)

// 进度计算
const progressPercent = computed(() => {
  const statusMap = {
    'draft': 0,
    'text_uploaded': 14,
    'settings_extracted': 28,
    'setting_images_generated': 42,
    'script_generated': 56,
    'voice_generated': 70,
    'storyboard_generated': 84,
    'videos_generated': 92,
    'completed': 100
  }
  return statusMap[projectStore.project?.status] || 0
})

const progressStatus = computed(() => {
  if (projectStore.project?.status === 'completed') return 'success'
  return ''
})

const progressText = computed(() => {
  const completed = steps.filter(s => isStepCompleted(s.key)).length
  return `已完成 ${completed}/${steps.length} 个步骤`
})

// 状态相关方法
const getStatusType = (status) => {
  const typeMap = {
    'draft': 'info',
    'text_uploaded': 'warning',
    'settings_extracted': 'warning',
    'script_generated': 'warning',
    'voice_generated': 'warning',
    'images_generated': 'warning',
    'videos_generated': 'warning',
    'completed': 'success'
  }
  return typeMap[status] || 'info'
}

const getStatusLabel = (status) => {
  const labelMap = {
    'draft': '草稿',
    'text_uploaded': '已上传',
    'settings_extracted': '已提取设定',
    'setting_images_generated': '已生成设定图',
    'script_generated': '已生成剧本',
    'voice_generated': '已生成语音',
    'storyboard_generated': '已生成分镜',
    'videos_generated': '已生成视频',
    'completed': '已完成',
    // 中间状态
    'setting_image_generation': '设定图生成中'
  }
  return labelMap[status] || status
}

// 步骤状态判断
const isStepCompleted = (stepKey) => {
  const statusOrder = [
    'draft',
    'text_uploaded',
    'settings_extracted',
    'setting_images_generated',
    'script_generated',
    'voice_generated',
    'storyboard_generated',
    'videos_generated',
    'completed'
  ]

  const currentStatusIndex = statusOrder.indexOf(projectStore.project?.status)
  const stepIndex = steps.findIndex(s => s.key === stepKey)

  return stepIndex < currentStatusIndex
}

const isStepAvailable = (stepKey) => {
  const statusOrder = [
    'draft',
    'text_uploaded',
    'settings_extracted',
    'setting_images_generated',
    'script_generated',
    'voice_generated',
    'storyboard_generated',
    'videos_generated',
    'completed'
  ]

  const currentStatusIndex = statusOrder.indexOf(projectStore.project?.status)
  const stepIndex = steps.findIndex(s => s.key === stepKey)

  return stepIndex <= currentStatusIndex + 1
}

// 步骤操作
const selectStep = (stepKey) => {
  if (!isStepAvailable(stepKey)) {
    ElMessage.warning('请先完成前面的步骤')
    return
  }
  currentStep.value = stepKey
}

const goToPrevStep = () => {
  if (hasPrevStep.value) {
    currentStep.value = steps[currentStepIndex.value - 1].key
  }
}

const goToNextStep = () => {
  if (hasNextStep.value) {
    currentStep.value = steps[currentStepIndex.value + 1].key
  }
}

// 步骤事件处理
const handleStepComplete = async () => {
  await projectStore.fetchProject()
  if (hasNextStep.value) {
    goToNextStep()
  }
}

const handleStepSave = () => {
  ElMessage.success('保存成功')
}

// 项目操作
const goBack = () => {
  router.push({ name: 'dashboard' })
}

const saveProject = async () => {
  try {
    await projectStore.fetchProject()
    ElMessage.success('项目已保存')
  } catch (error) {
    ElMessage.error('保存失败')
  }
}

const editProject = () => {
  showProjectInfo.value = false
  // 可以打开编辑对话框
}

const composeFinalVideo = async () => {
  composing.value = true
  try {
    // 调用合成API
    ElMessage.success('开始合成最终视频')
  } catch (error) {
    ElMessage.error('合成失败')
  } finally {
    composing.value = false
  }
}

// 格式化日期
const formatDate = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleString('zh-CN')
}

// 初始化
onMounted(async () => {
  projectStore.setProjectId(projectId.value)
  try {
    await projectStore.fetchProject()
    // 根据项目状态设置当前步骤
    const statusStepMap = {
      'draft': 'text',
      'text_uploaded': 'settings',
      'settings_extracted': 'settingGallery',
      'setting_images_generated': 'script',
      'script_generated': 'voice',
      'voice_generated': 'storyboard',
      'storyboard_generated': 'videos',
      'videos_generated': 'compose',
      'completed': 'compose'
    }
    currentStep.value = statusStepMap[projectStore.project?.status] || 'text'
  } catch (error) {
    ElMessage.error('加载项目失败')
    router.push({ name: 'dashboard' })
  }
})
</script>

<style scoped lang="scss">
.workspace-view {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f5f7fa;
}

.workspace-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: #fff;
  border-bottom: 1px solid #e4e7ed;

  .header-left {
    display: flex;
    align-items: center;
    gap: 16px;

    .project-info {
      display: flex;
      align-items: center;
      gap: 12px;

      .project-title {
        font-size: 18px;
        font-weight: 600;
        color: #303133;
        margin: 0;
      }
    }
  }
}

.workspace-body {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.step-sidebar {
  width: 280px;
  background: #fff;
  border-right: 1px solid #e4e7ed;
  display: flex;
  flex-direction: column;
  padding: 20px;

  .step-list {
    flex: 1;
  }

  .step-item {
    display: flex;
    align-items: flex-start;
    padding: 16px;
    margin-bottom: 8px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s;

    &:hover:not(.disabled) {
      background: #f5f7fa;
    }

    &.active {
      background: #ecf5ff;
      border-left: 3px solid #409eff;
    }

    &.completed {
      .step-number {
        background: #67c23a;
        color: #fff;
      }
    }

    &.disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .step-number {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      background: #e4e7ed;
      color: #606266;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      font-weight: 600;
      margin-right: 12px;
      flex-shrink: 0;
    }

    .step-content {
      flex: 1;

      .step-title {
        font-size: 14px;
        font-weight: 500;
        color: #303133;
        margin-bottom: 4px;
      }

      .step-desc {
        font-size: 12px;
        color: #909399;
      }
    }

    .step-check {
      color: #67c23a;
      font-size: 16px;
    }
  }

  .progress-section {
    padding-top: 20px;
    border-top: 1px solid #e4e7ed;

    h4 {
      font-size: 14px;
      color: #606266;
      margin: 0 0 12px;
    }

    .progress-text {
      font-size: 12px;
      color: #909399;
      margin: 8px 0 0;
      text-align: center;
    }
  }
}

.content-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .content-header {
    padding: 20px 24px;
    background: #fff;
    border-bottom: 1px solid #e4e7ed;

    h3 {
      font-size: 18px;
      font-weight: 600;
      color: #303133;
      margin: 0 0 8px;
    }

    .step-hint {
      font-size: 14px;
      color: #909399;
      margin: 0;
    }
  }

  .content-body {
    flex: 1;
    overflow-y: auto;
    padding: 24px;
  }

  .content-footer {
    display: flex;
    justify-content: space-between;
    padding: 16px 24px;
    background: #fff;
    border-top: 1px solid #e4e7ed;
  }
}
</style>